"use client"

import { useFlow } from "@/hooks"
import { useEffect, useRef, useState } from "react"
import { useFormContext } from "react-hook-form"

import {
  analyticsFlowNameByActualFlowName,
  analyticsPageByFlow,
  FlowKey,
} from "@/config/flows"
import { useLowercaseTranslation } from "@/hooks/use-lowercase-translation"
import { getDifferences } from "@/lib/analytics"
import { transformError } from "@/lib/validations/transform-error"
import { AnalyticsDataLayer } from "@/store/analytics-store"

import { useAnalyticsContext } from "./analytics-provider"

/* -------------------------------------------------------------------------------------------------
 * types
 * -----------------------------------------------------------------------------------------------*/

type AnalyticsFormProps = {
  analyticsMap: Record<string, string>
  conversion_pagename?: string
}

/* -------------------------------------------------------------------------------------------------
 * component
 * -----------------------------------------------------------------------------------------------*/

const AnalyticsForm = ({
  analyticsMap,
  conversion_pagename,
}: AnalyticsFormProps) => {
  /*--------------------hooks--------------------*/
  const { formState } = useFormContext()
  const t = useLowercaseTranslation()
  const [lastErrorState, setLastErrorState] = useState<string>("{}")
  const strFormStateErrors = JSON.stringify(formState.errors)
  const { sendData } = useAnalyticsContext()

  useEffect(() => {
    /* this need some explaination for sure so here you have:

      We keep triggering this useEffect when there is a change in strFormStateErrors (actually formState.errors)
      so we check the differences between the current formState.errors and lastErrorState,
      if there is any differences we send an analytic event with only the differences, 
      this is needed to avoid sending the same error on a field that has not changed his error from last analytics sent.
      Lastly we save the entire formState.errors as lastErrorState in case we have at least one touched field
    */

    const sendAnalytics = setTimeout(() => {
      const diffFieldsWithErrors = getDifferences(
        JSON.parse(lastErrorState),
        JSON.parse(strFormStateErrors),
      )

      if (Object.keys(diffFieldsWithErrors).length) {
        let mustUpdateLastErrorState = false
        for (const fieldWithError in diffFieldsWithErrors) {
          const isFieldTouched = formState.touchedFields[fieldWithError]
          if (isFieldTouched) {
            mustUpdateLastErrorState = true
            const fieldNameForAnalytics = analyticsMap[fieldWithError] //TODO sistemare per rendere analyticsMap opzionale
              ? analyticsMap[fieldWithError]
              : fieldWithError

            const { message, messageProps } = transformError(
              diffFieldsWithErrors[fieldWithError].message ??
                "Errors.emptyError",
            )
            if (message && message !== "Errors.emptyError") {
              sendData({
                event: "link_click",
                event_name: "form_error",
                error_message: t(message, messageProps),
                error_type: fieldNameForAnalytics,
                ...(conversion_pagename && { conversion_pagename }),
              })
            }
          }
        }

        if (mustUpdateLastErrorState) {
          setLastErrorState(strFormStateErrors)
        }
      }
    }, 1200)

    return () => clearTimeout(sendAnalytics)
  }, [
    strFormStateErrors,
    formState.touchedFields,
    lastErrorState,
    analyticsMap,
    t,
    sendData,
    conversion_pagename,
  ])

  return null
}

const AnalyticsLoadedPage = ({
  data,
  step,
  flowName,
}: {
  data?: Partial<AnalyticsDataLayer> & Record<string, string | number>
  step?: string
  flowName?: FlowKey
}) => {
  const hasBeenTracked = useRef<boolean>(false)
  const { sendData } = useAnalyticsContext()
  const { actualFlowName } = useFlow()
  useEffect(() => {
    const flowConfig =
      analyticsPageByFlow[flowName || actualFlowName || "no-flow"]
    const flowCongigLoadedPage =
      step && step in flowConfig ? flowConfig[step] : {}

    const flusso_registrazione =
      flowCongigLoadedPage?.flusso_registrazione ||
      analyticsFlowNameByActualFlowName[actualFlowName || "no-flow"] ||
      ""

    const analyticsPayload = {
      page_name: flowCongigLoadedPage?.page_name,
      conversion_pagename: flowCongigLoadedPage?.conversion_pagename,
      flusso_registrazione:
        flusso_registrazione === "no-flow" ? "" : flusso_registrazione,
      ...data,
    }

    if (!hasBeenTracked.current) {
      hasBeenTracked.current = true
      sendData({ loaded_page: "yes", ...analyticsPayload })
    }
  }, [actualFlowName, data, sendData, step, flowName])

  return null
}

export { AnalyticsForm, AnalyticsLoadedPage }
